'use client';
import React from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {
  ShieldCheck,
  Phone,
  AlertTriangle,
  FileText,
  ChevronRight,
  CheckCircle,
  HelpCircle,
} from 'lucide-react';

import { useSiteSettings } from '@/components/SiteSettingsProvider';

export default function CancellationPolicyPage() {
  const { phoneNumber, showPhoneNumber } = useSiteSettings();
  return (
    <div className="min-h-screen font-sans bg-slate-50 text-slate-800 flex flex-col">
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <div className="relative pt-[120px] pb-20 bg-slate-950 overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-slate-950 to-slate-950 z-0" />
        <div className="absolute top-[-100px] right-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none z-0" />

        <div className="relative z-10 max-w-[1170px] mx-auto px-6 w-full text-left">
          <div
            style={{ height: '30px' }}
            className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-1 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 w-fit backdrop-blur-md"
          >
            TICKET CANCELLATIONS
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white uppercase tracking-tight leading-none mb-4">
            Cancellation{' '}
            <span className="bg-gradient-to-r from-accent via-accent-light to-blue-500 bg-clip-text text-transparent">
              Policy
            </span>
          </h1>
          <p className="text-slate-400 text-xs md:text-sm font-semibold max-w-2xl leading-relaxed">
            Review the terms for booking cancellations, emergency modifications by airlines, and
            phone-only waiver processing.
          </p>
        </div>
      </div>

      {/* Main Grid Content */}
      <div className="flex-grow max-w-[1170px] mx-auto px-6 py-16 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Main cancellation terms (Left) */}
          <div className="lg:col-span-8 text-left space-y-8 animate-fadeInUp">
            {/* General Policy */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-5.5 h-5.5 text-accent" />
                1. Important Booking Modifications & Cancellations
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                Exceptionally as a result of environmental changes or political problems, adjourn of
                times or air routes, and different situations that are not in our control, we may
                have some circumstances to make changes to and can cancel already confirmed
                bookings. While we try our best to ward off changes and cancellations, therefore we
                must have the right to do so.
              </p>

              <div className="p-4 bg-slate-50 border border-slate-200/60 rounded-2xl">
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-wider mb-1">
                  What is an "Important Change"?
                </h4>
                <p className="text-slate-500 text-xs font-semibold leading-relaxed">
                  An important change is a change that is made before your departure, in accordance
                  with the information you provided us at the time of your travel booking, which may
                  have some dominant effect on your confirmed trip.
                </p>
              </div>

              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                If we have to make an important change or cancellation, we will inform you as soon
                as possible. In these situations, we will provide you other routing or dates and
                somewhat one option which is of same cost and standard if airline confirms the same.
                If time allows doing so before departure, we will provide you following options to
                choose:
              </p>

              <ul className="space-y-4">
                <li className="flex gap-3 items-start text-slate-600 text-xs font-semibold leading-relaxed">
                  <CheckCircle className="w-4.5 h-4.5 text-accent flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Acceptance</strong> of changed adjustments.
                  </span>
                </li>
                <li className="flex gap-3 items-start text-slate-600 text-xs font-semibold leading-relaxed">
                  <CheckCircle className="w-4.5 h-4.5 text-accent flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Alternative Trip:</strong> You can purchase an alternative trip of the
                    same standard to that of your original booking if available. The alternative
                    trip would be considered as a new purchase and we will coordinate with the
                    airlines to get you compensation or refund of your existing booking. You must
                    pay the relevant price of any such options adjustments.
                  </span>
                </li>
                <li className="flex gap-3 items-start text-slate-600 text-xs font-semibold leading-relaxed">
                  <CheckCircle className="w-4.5 h-4.5 text-accent flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Full Refund:</strong> Accepting cancellation, in which case you will
                    receive a full refund of all the costs you have paid to us. This will be
                    available if airlines allow us to do so.
                  </span>
                </li>
              </ul>

              <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-2xl text-[11px] font-bold text-blue-800 leading-normal">
                ⚠️ <strong>Note:</strong> The changes mentioned above don’t include changes made in
                the case of minor ones.
              </div>
            </div>

            {/* Circumstances Beyond Control */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider">
                2. Circumstances Beyond Control
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                When airlines make any cancellation or change as per circumstances which are not
                under control and whose consequences cannot be avoided even with all due care, we
                will not provide you any compensation or take responsibilities of the choices
                mentioned above. We can discuss the situations with airlines on your behalf and ask
                them to provide reimbursement. But the whole decision depends on airlines; we cannot
                guarantee any such refund or compensations.
              </p>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                If we have to make changes or cancellation as a result of your failure to abide with
                the requirements of bookings (such as not paying on time), we will not be
                responsible to pay any compensation mentioned above.
              </p>
            </div>

            {/* Phone-Only Cancellations */}
            <div className="bg-white border border-slate-200/60 rounded-3xl p-8 shadow-sm space-y-6">
              <h2 className="text-slate-800 text-lg font-black uppercase tracking-wider flex items-center gap-2">
                <HelpCircle className="w-5.5 h-5.5 text-accent" />
                3. Alteration and Cancellations Derivable to You
              </h2>
              <p className="text-slate-600 text-xs md:text-sm font-medium leading-relaxed">
                All the bookings of airline tickets are mostly non-refundable unless it is
                specifically described otherwise.
              </p>

              <div className="p-5 bg-slate-50 border border-slate-200/60 rounded-2xl space-y-3.5 text-xs text-slate-600 font-semibold leading-relaxed">
                <p className="font-bold text-slate-800">🗣️ Phone-Only Cancellation Requirement:</p>
                <p>
                  All cancellations must be requested over the phone only by calling {phoneNumber}. Cancellations submitted via email are not automatically processed
                  and require verbal confirmation.
                </p>
                <p>
                  Once you submit your cancellation request to our customer care agent, you will
                  receive an email notification acknowledging your request. This email does not mean
                  that you are qualified for a refund.
                </p>
                <p>
                  Based on the rules of the fare, we have to work with the airline or other related
                  suppliers to create a waiver and process your refund if fare rules allow it.
                  Refund processing times vary but typically take a minimum of{' '}
                  <strong>4-6 business weeks</strong> to process.
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar widget (Right) */}
          <div className="lg:col-span-4 sticky top-[110px] space-y-6">
            {/* VIP Desk Card */}
            {showPhoneNumber && (
              <div className="bg-white border border-slate-200/60 rounded-3xl p-6 shadow-sm text-left space-y-6">
                <div>
                  <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                    Cancellation Desk
                  </h4>
                  <p className="text-slate-500 text-xs font-semibold leading-relaxed mb-4">
                    Speak directly with an agent to request a ticket cancellation or verify airline
                    flight changes.
                  </p>

                  <a
                    href={`tel:${phoneNumber}`}
                    className="flex items-center gap-3.5 bg-slate-950 hover:bg-slate-900 border border-white/10 rounded-2xl p-4 shadow-premium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] group"
                  >
                    <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-blue-600 shadow-glow">
                      <span className="absolute inset-0 rounded-xl bg-accent animate-ping opacity-20"></span>
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">
                        TOLL-FREE 24/7
                      </div>
                      <div className="text-sm font-black text-white mt-1 tracking-tight leading-none group-hover:text-accent-light transition-colors">
                        {phoneNumber}
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            )}

              <div>
                <h4 className="text-slate-800 text-xs font-black uppercase tracking-widest mb-4 pb-2 border-b border-slate-100 block">
                  Reliability Guarantee
                </h4>
                <div className="flex items-start gap-3 bg-slate-50 border border-slate-200/60 p-4 rounded-2xl">
                  <ShieldCheck className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="text-slate-800 text-xs font-bold leading-tight">
                      Expert Coordination
                    </h5>
                    <p className="text-slate-400 text-[10px] font-semibold mt-1 leading-normal">
                      We communicate directly with airlines to secure the best possible waivers for
                      you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


      <Footer />
    </div>
  );
}
